import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ["details"];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settledTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
    clearTimeout(this.#settledTimeout);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== "Escape") return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest("details") ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "true");
    requestAnimationFrame(() => details.classList.add("menu-open"));

    if (details === this.refs.details) {
      details.classList.remove("menu-drawer--settled");
      this.#scheduleSettled(details);
    }

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "false");
    details.classList.remove("menu-open");

    if (details === this.refs.details) {
      details.classList.remove("menu-drawer--settled");
      clearTimeout(this.#settledTimeout);
    }

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll("details[open]");
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty("will-change", "unset");
        target.removeEventListener(
          "animationend",
          removeWillChangeOnAnimationEnd,
        );
      }
    }
    const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
    allAnimated.forEach((element) => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * After the drawer open stagger finishes, skip it on later accordion expands.
   * @param {HTMLDetailsElement} details
   */
  #scheduleSettled(details) {
    clearTimeout(this.#settledTimeout);

    const styles = getComputedStyle(details);
    const duration =
      Number.parseFloat(
        styles.getPropertyValue("--menu-drawer-item-duration"),
      ) || 0.12;
    const delayStart =
      Number.parseFloat(
        styles.getPropertyValue("--menu-drawer-item-delay-start"),
      ) || 0.05;
    const stagger =
      Number.parseFloat(
        styles.getPropertyValue("--menu-drawer-item-stagger"),
      ) || 0.035;
    let maxIndex = 1;

    details
      .querySelectorAll(".menu-drawer__animated-element")
      .forEach((element) => {
        const index = Number.parseFloat(
          getComputedStyle(element).getPropertyValue(
            "--menu-drawer-animation-index",
          ),
        );
        if (!Number.isNaN(index) && index > maxIndex) maxIndex = index;
      });

    const waitMs =
      (delayStart + (maxIndex - 1) * stagger + duration) * 1000 + 50;

    this.#settledTimeout = setTimeout(() => {
      details.classList.add("menu-drawer--settled");
    }, waitMs);
  }
}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove("menu-open");
  element.classList.remove("menu-drawer--settled");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}
